/**
 * DÍA 6 — Human Conversation.
 *
 * Transferencia social auténtica. Ideal: 30–45 minutos con una persona real.
 * No tiene que ser una clase.
 *
 * ADVERTENCIA OBLIGATORIA (CURRICULUM.md §11): si no hay persona real, ChatGPT
 * sirve de respaldo, pero la aplicación debe dejar claro que NO equivale a una
 * interacción humana real. v2_2 lo decía solo en la interfaz del modo foco;
 * acá está en el contenido del paso, que es donde se lee.
 */

import type { Sesion } from '../../tipos.js';

const NO_ES_EQUIVALENTE =
  'Si hoy no conseguís una persona real, usá ChatGPT como respaldo. Pero que quede claro: NO es equivalente. Una persona real tiene intenciones propias, no te tiene paciencia infinita, se distrae, y no sabe que esto es un ejercicio. Esa diferencia es justamente lo que estamos midiendo hoy.';

export const D6: Sesion = {
  clave: 'd6',
  tipo: 'dia',
  etiqueta: 'Día 6 · Conversación humana',
  titulo: 'Día 6 · Transferencia con una persona real',
  corto: 'Humano',
  objetivo:
    'Comprobar que tu inglés funciona con una persona que no conoce el ejercicio.',
  porQue:
    'La interacción humana añade imprevisibilidad social, listening y turn-taking que conviene conservar junto a la práctica con IA. Es la prueba de que lo entrenado sale del entrenamiento.',
  areasAEvaluar: ['interaccion_turnos', 'listening'],
  capturaCorrecciones: false,
  planes: {
    20: {
      duracion: 20,
      pasos: [
        {
          clave: 'd6-conversacion',
          titulo: 'Conversación real',
          minutosSugeridos: 20,
          herramienta: 'humano',
          prompt: 'd6',
          registraPersonaReal: true,
          objetivo: 'Hablar en inglés con una persona real.',
          porQue:
            'Es una prueba de transferencia, no una clase. Nadie te va a corregir y eso es parte del punto.',
          instrucciones: [
            'Hablá de un tema cotidiano.',
            'No prepares frases de antemano.',
            'Concentrate en entender y reaccionar, no en sonar perfecto.',
            'Veinte minutos es el mínimo útil: lo ideal son 30 a 45. Si podés estirarlo, estiralo.',
          ],
          uso: {
            queHacer:
              'Conversá en inglés con una persona real sobre algo cotidiano. Si no es posible hoy, abrí ChatGPT con el prompt de respaldo.',
          },
          advertencia: NO_ES_EQUIVALENTE,
          areas: ['interaccion_turnos', 'listening', 'habla_espontanea'],
        },
      ],
    },

    35: {
      duracion: 35,
      pasos: [
        {
          clave: 'd6-conversacion',
          titulo: 'Conversación real',
          minutosSugeridos: 30,
          herramienta: 'humano',
          prompt: 'd6',
          registraPersonaReal: true,
          objetivo: 'Practicar interacción social auténtica.',
          porQue:
            'La otra persona tiene su propio ritmo e intenciones. No se adapta a vos como lo hace una IA.',
          instrucciones: [
            'Tema cotidiano, sin preparar.',
            'Concentrate en comprender y reaccionar, no en sonar perfecto.',
            'Si no entendés algo, pedí que lo repitan: eso también es el ejercicio.',
          ],
          uso: {
            queHacer:
              'Conversá 30 minutos en inglés con una persona real. Si no es posible hoy, abrí ChatGPT con el prompt de respaldo.',
          },
          advertencia: NO_ES_EQUIVALENTE,
          areas: ['interaccion_turnos', 'listening', 'habla_espontanea'],
        },
        {
          clave: 'd6-hallazgos',
          titulo: 'Qué fue distinto',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 5 minutos.',
          objetivo: 'Notar qué fue más difícil que con la IA.',
          porQue:
            'Esa diferencia nos dice qué hay que simular mejor durante la semana. Es la información más valiosa del día.',
          instrucciones: [
            'Guardá entre 1 y 3 hallazgos, no más.',
            '¿Qué entendiste peor? ¿Dónde te costó entrar al turno?',
            'Guardalos en el Learning Bank con su área si la reconocés.',
          ],
        },
      ],
    },

    50: {
      duracion: 50,
      pasos: [
        {
          clave: 'd6-conversacion',
          titulo: 'Conversación real',
          minutosSugeridos: 40,
          herramienta: 'humano',
          prompt: 'd6',
          registraPersonaReal: true,
          objetivo: 'Sostener interacción auténtica el tiempo suficiente.',
          porQue:
            'Más tiempo permite adaptación y turn-taking reales. En diez minutos todavía estás en la fase cortés.',
          instrucciones: [
            'No conviertas la conversación en un ejercicio.',
            'Reaccioná y preguntá con naturalidad.',
            'Dejá que el tema derive.',
          ],
          uso: {
            queHacer:
              'Conversá 40 minutos en inglés con una persona real, sin convertirlo en clase. Si no es posible, abrí ChatGPT con el prompt de respaldo.',
          },
          advertencia: NO_ES_EQUIVALENTE,
          areas: ['interaccion_turnos', 'listening', 'habla_espontanea'],
        },
        {
          clave: 'd6-comparacion',
          titulo: 'Comparación con la IA',
          minutosSugeridos: 10,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 10 minutos.',
          objetivo: 'Extraer información útil de la diferencia.',
          porQue:
            'La distancia entre hablar con una persona y con una IA es lo que debemos cerrar. Sin registrarla, se olvida.',
          instrucciones: [
            '¿Qué entendiste peor que con la IA?',
            '¿Dónde te costó entrar al turno?',
            '¿Qué diste por sentado que la IA te perdonaba y una persona no?',
            'Guardá como máximo 3 hallazgos.',
          ],
        },
      ],
    },
  },
};
